import { FC, useCallback, useContext, memo, useMemo, useEffect, useRef } from 'react'
import { ThemeContext } from 'styled-components'
import {
    SearchbarContainer,
    SearchbarContent,
    SearchButton,
    SubmitButton,
    StickyContainer,
    StickySearchbarContainer,
    Spinner,
    MobilePillContainer,
    MobilePill,
} from './styles'
import { useTranslation } from '../../../hooks/locale'
import { Location, SearchbarMode } from '../../search-result-listing/header-section/types'
import SearchbarGuestPicker from '../../search-result-listing/header-section/searchbar/guest-picker'
import SearchbarDatePicker from '../../search-result-listing/header-section/searchbar/date-picker'
import RegionsAutocompleteSuggestionPicker from '../../search-result-listing/header-section/searchbar/regions-autocomplete-suggestion-picker'
import SearchIcon from '../../../assets/icons/userbar-icons/search.svg'
import AnalyticsService from '../../../services/analytics-service'
import { LocationPickerMode } from '../../common/location-picker/types'
import { useAppDispatch } from '../../../redux/hooks'
import { useSearchUrl } from '../../../hooks/search'
import { saveHistoryItem } from '../../../redux/searchbar'
import { getHistoryObject } from '../../landing-page/utils'
import { getSearchDate } from '../../../utils/miscellaneous-utils'
import { commonPageEventActions } from '../../../services/analytics-service/actions'
import { CommonPageEventCategories } from '../../../services/analytics-service/categories'
import { getFilterFromHref } from '../../../utils/request-utils'
import updateDateRetentionCookies from '../../property-details/utils/update-date-retention-cookies'
import { HeaderSectionTransport } from '../../../transports/region-landing-page-transport'
import { useControlledSearchbarLogic } from '../../../hooks/controlled-seachbar-logic'
import LogoArea from '../header-section/logo-area'
import {
    getLocationPickerLabel,
    getDatePickerLabel,
    getGuestPickerLabel,
} from '../../search-result-listing/header-section/searchbar/utils'
import { useDatePickerType } from '../../common/date-picker/hooks'
import AllInputsModal from '../../search-result-listing/header-section/searchbar/all-inputs-modal'
import { useModal } from '../../../hooks/modal'
import { PageSubType } from '../../../constants/booking-page-type'
import { ScreenContext } from 'react-components'

type Navigation = HeaderSectionTransport['navigationSectionResponse']['navigation']

interface ControlledSearchbarProps {
    // Controlled values - ALL from parent
    location: Location
    arrivalDate: Date | null
    departureDate: Date | null
    adultsCount: number
    childrenCount: number
    // Handlers - call parent
    onLocationChange: (location: Location) => void
    onArrivalDateChange: (date: Date | null) => void
    onDepartureDateChange: (date: Date | null) => void
    onAdultsCountChange: (count: number) => void
    onChildrenCountChange: (count: number) => void
    // Other props
    petsAllowed?: boolean
    isSticky?: boolean
    logo?: Navigation['logo']
    maxWidth?: string
    searchbarMode?: SearchbarMode
    pageSubType?: PageSubType
    isBorderGradientEnabled?: boolean
}

const ControlledSearchbar: FC<ControlledSearchbarProps> = ({
    location,
    arrivalDate,
    departureDate,
    adultsCount,
    childrenCount,
    onLocationChange,
    onArrivalDateChange,
    onDepartureDateChange,
    onAdultsCountChange,
    onChildrenCountChange,
    petsAllowed = false,
    isSticky = false,
    logo,
    maxWidth,
    searchbarMode = SearchbarMode.MAIN,
    pageSubType,
    isBorderGradientEnabled,
}) => {
    const dispatch = useAppDispatch()
    const { getSearchUrl } = useSearchUrl()
    const { t } = useTranslation()
    const theme = useContext(ThemeContext)!

    // Mobile modal state for AllInputsModal
    const mobileModal = useModal(false)

    const {
        isLocationPickerOpen,
        isDatePickerOpen,
        isGuestPickerOpen,
        onLocationPickerClose,
        onDatePickerClose,
        onDatePickerOpen,

        locationPickerError,
        setLocationPickerError,

        isSubmitting,
        setIsSubmitting,

        handleLocationPickerOpen,
        handleLocationInputChange,
        handleLocationInputSelect,
        handleLocationItemSelect,
        handleHistoryItemSelect,

        handleGuestPickerOpen,
        handleGuestPickerClose,

        trackArrivalDateChange,
        trackDepartureDateChange,
        handleDepartureDateChange,
        localSearch,
    } = useControlledSearchbarLogic({
        location,
        arrivalDate,
        departureDate,
        adultsCount,
        childrenCount,
        onLocationChange,
        onArrivalDateChange,
        onDepartureDateChange,
        onAdultsCountChange,
        onChildrenCountChange,
    })

    const { isMobile } = useContext(ScreenContext)

    const _hasAutoOpenedCalendar = useRef(false)

    useEffect(() => {
        if (_hasAutoOpenedCalendar.current) {
            return
        }
        if ((!arrivalDate || !departureDate) && location.search && searchbarMode === SearchbarMode.PILL) {
            _hasAutoOpenedCalendar.current = true
            if (isMobile) {
                mobileModal.open()
            } else {
                onDatePickerOpen()
            }
        }
    }, [location])

    const _handleSubmit = useCallback(async () => {
        if (location.search.trim() === '') {
            setLocationPickerError(t('locationPickerError'))
            return
        }

        // Set loading state for visual feedback
        setIsSubmitting(true)

        const arrival = getSearchDate(arrivalDate)
        const departure = getSearchDate(departureDate)

        AnalyticsService.trackEvent({
            action: commonPageEventActions.searchSection.searchBox.CLICK_SEARCH,
            category: CommonPageEventCategories.searchSection.SEARCH_BOX,
            label: location.search,
            ga_custom_param_arrival: arrival,
            ga_custom_param_departure: departure,
            ga_custom_param_person: adultsCount + childrenCount,
        })

        AnalyticsService.trackEvent({
            event: commonPageEventActions.searchSection.searchBox.SEARCH,
            eventValue: location.search,
        })

        dispatch(
            saveHistoryItem(getHistoryObject(arrivalDate, departureDate, adultsCount, childrenCount, location.search)),
        )
        updateDateRetentionCookies(arrival, departure, adultsCount, childrenCount, location.search)

        // Get search URL and navigate immediately
        try {
            const searchUrl = await getSearchUrl(getFilterFromHref(globalThis.location.href), {
                regionId: location.regionId,
                search: location.search,
                arrival,
                departure,
                adults: adultsCount,
                children: childrenCount,
            })

            setIsSubmitting(false)

            globalThis.location.href = `${globalThis.location.origin}${searchUrl}`
        } catch (error) {
            // Reset loading state on error
            setIsSubmitting(false)
            console.error('Search URL generation failed:', error)
        }
    }, [
        location,
        arrivalDate,
        departureDate,
        adultsCount,
        childrenCount,
        dispatch,
        getSearchUrl,
        t,
        setIsSubmitting,
        setLocationPickerError,
    ])

    // Mobile pill labels
    const datePickerType = useDatePickerType({ endDate: departureDate })
    const _locationPickerLabel = getLocationPickerLabel(location, t('whereTo'))
    const _datePickerLabel = getDatePickerLabel({
        startDate: arrivalDate,
        endDate: departureDate,
        startDatePlaceholder: t('checkIn'),
        endDatePlaceholder: t('checkOut'),
        datePickerType,
    })

    const _guestPickerLabel = getGuestPickerLabel(adultsCount + childrenCount, t('guest'), t('guests'), t('addGuests'))

    // Calculate widget height based on searchbarMode
    const widgetHeight = searchbarMode === SearchbarMode.PILL ? '2rem' : '3rem'

    // Memoized border styles for picker components
    const borderStyles = useMemo(
        () => ({
            locationPicker: `border-right: ${theme.typography.border.width.xxxThin} solid ${theme.colors.tfwBlackOpacity10};
                           @media only screen and (max-width: 767px) {
                               border-right: none;
                               border-bottom: ${theme.typography.border.width.xxxThin} dotted ${theme.colors.tfwBlackOpacity10};
                           }`,
            datePicker: `border-right: ${theme.typography.border.width.xxxThin} solid ${theme.colors.tfwBlackOpacity10};
                       @media only screen and (max-width: 767px) {
                           border-right: none;
                           border-bottom: ${theme.typography.border.width.xxxThin} dotted ${theme.colors.tfwBlackOpacity10};
                       }`,
            guestPicker: 'border: none;',
        }),
        [theme],
    )

    const mobilePill = (
        <MobilePillContainer searchbarMode={searchbarMode}>
            <MobilePill onClick={mobileModal.open}>
                {_locationPickerLabel} &middot; {_datePickerLabel} &middot; {_guestPickerLabel}
            </MobilePill>
        </MobilePillContainer>
    )

    const searchbarContent = (
        <SearchbarContainer
            maxWidth={maxWidth}
            searchbarMode={searchbarMode}
            isBorderGradientEnabled={isBorderGradientEnabled}
        >
            <SearchbarContent maxWidth={maxWidth} searchbarMode={searchbarMode}>
                {mobilePill}
                <RegionsAutocompleteSuggestionPicker
                    isOpen={isLocationPickerOpen}
                    onOpen={handleLocationPickerOpen}
                    onClose={onLocationPickerClose}
                    locationInput={localSearch}
                    onLocationInputChange={handleLocationInputChange}
                    onLocationInputSelect={handleLocationInputSelect}
                    onLocationItemSelect={handleLocationItemSelect}
                    onHistoryItemSelect={handleHistoryItemSelect}
                    error={locationPickerError}
                    mode={SearchbarMode.PILL === searchbarMode ? undefined : LocationPickerMode.WIDGET}
                    widgetHeight={widgetHeight}
                    border={borderStyles.locationPicker}
                    pageSubType={pageSubType}
                />

                <SearchbarDatePicker
                    isOpen={isDatePickerOpen}
                    onOpen={onDatePickerOpen}
                    onClose={onDatePickerClose}
                    startDate={arrivalDate}
                    endDate={departureDate}
                    onStartDateChange={onArrivalDateChange}
                    onEndDateChange={handleDepartureDateChange}
                    trackStartDateChange={trackArrivalDateChange}
                    trackEndDateChange={trackDepartureDateChange}
                    showInputInModal={searchbarMode === SearchbarMode.MAIN}
                    widgetHeight={widgetHeight}
                    border={borderStyles.datePicker}
                />

                <SearchbarGuestPicker
                    isOpen={isGuestPickerOpen}
                    onOpen={handleGuestPickerOpen}
                    onClose={handleGuestPickerClose}
                    adultsCount={adultsCount}
                    childrenCount={childrenCount}
                    onAdultsCountChange={onAdultsCountChange}
                    onChildrenCountChange={onChildrenCountChange}
                    petsAllowed={petsAllowed}
                    showInputInModal={searchbarMode === SearchbarMode.MAIN}
                    widgetHeight={widgetHeight}
                    border={borderStyles.guestPicker}
                />
                <AllInputsModal
                    isOpen={mobileModal.isOpen}
                    onLocationPickerOpen={handleLocationPickerOpen}
                    onDatePickerOpen={onDatePickerOpen}
                    onGuestPickerOpen={handleGuestPickerOpen}
                    locationPickerLabel={_locationPickerLabel}
                    datePickerLabel={_datePickerLabel}
                    guestPickerLabel={_guestPickerLabel}
                    locationPickerError={locationPickerError}
                    onClose={mobileModal.close}
                    onSubmit={_handleSubmit}
                    isSubmitting={isSubmitting}
                />

                <SearchButton
                    padding={theme.spacing.xSmall + ' ' + theme.spacing.medium}
                    onClick={_handleSubmit}
                    showMainSearchBar={searchbarMode === SearchbarMode.MAIN}
                    disabled={isSubmitting}
                    searchbarMode={searchbarMode}
                >
                    {isSubmitting ? (
                        <Spinner />
                    ) : (
                        <>
                            <SearchIcon aria-label={t('search')} width={24} height={24} />
                            <span>{t('search')}</span>
                        </>
                    )}
                </SearchButton>

                {searchbarMode === SearchbarMode.MAIN && (
                    <SubmitButton onClick={_handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? <Spinner /> : t('searchButtonText')}
                    </SubmitButton>
                )}
            </SearchbarContent>
        </SearchbarContainer>
    )

    if (isSticky) {
        return (
            <StickyContainer>
                <StickySearchbarContainer>
                    {!!logo && <LogoArea logo={logo} />}
                    {searchbarContent}
                </StickySearchbarContainer>
            </StickyContainer>
        )
    }

    return searchbarContent
}

export default memo(ControlledSearchbar)
